import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { AppDispatch } from "index";
import { toast } from "react-toastify";
import {
  Form,
  Row,
  Col,
  Spinner,
  Button,
  Modal,
  InputGroup,
} from "react-bootstrap";
import { Autocomplete, Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { createSelector } from "reselect";
import { ChevronDown, ChevronUp } from "lucide-react";

import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import FormActionButtons from "pages/QuotationV2/Form/Components/FormActionButtons";
import ProductFormTable from "../../Components/ProductFormTable";
import EnquiryDetailsSection from "../../Components/EnquiryDetailsSection";
import CustomerDetailsSection from "../../Components/CustomerDetailsSection";
import CompanyFormModal from "../../Components/CompanyFormModal";
import AddProductModal from "pages/QuotationV2/Form/Components/AddProductModal";
import { fetchAllUsersDropdown } from "slices/UserManagement/user/thunk";
import { fetchCompanyByDropDown } from "slices/Masters/company/thunk";
import { initialEnquiryData } from "Common/CommonEnquiryData/EnquiryType";
import { MecordColors } from "Constants/Color";
import {
  fetchProductEnquiryById,
  createProductEnquiry,
  updateProductEnquiry,
  fetchSubcategories,
  fetchMakes,
  fetchModels,
  fetchCategories,
  fetchProductEnquiries,
} from "slices/enquiry/thunk";
import { appendToFormData } from "helpers/formDataHelper";
import LoadingSpinner from "components/LoadingSpinner";
import ENQPageContainer from "../../Components/ENQPageContainer";
import { ENQ_STATUS } from "Constants/StatusConstant";
import { autocompleteDropdownStyles } from "Constants/Style";
import { clearTableState } from "utils/clearTableStates";

import { TableKeys } from "Constants/TableKeys/TableKeys";
import { getCurrentLocalDate } from "helpers/CurrentDate/getCurrentLocalDate";
import FileUpload from "Common/Components/FileUpload.tsx/FileUpload";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import AddCustomerModal from "pages/Enquiry/Components/AddCustomerModal";
import AddTags from "pages/Enquiry/Components/AddTags";
import BillingFromAddress from "Common/DynamicComponentsForEnquiryQuotationSalesOrder/BillingFromAddress";

export const DEFAULT_ORGANISATION = {
  organization_id: "4f2e3a6c-5c4c-4445-92b6-6f64a26b1c78",
  organization_name: "MECORD SYSTEMS & SERVICES PVT. LTD.",
  org_address: "HILLVIEW INDUSTRIAL ESTATE, 304, off Lal Bahadur Shastri Marg, Amrut Nagar, Ghatkopar-W.",
  org_city: "Mumbai",
  org_state: "Maharashtra",
  org_country: "India",
  org_pincode: 400086
};

interface Company {
  company_id: string;
  company_name: string;
  branch_name?: string | null;
  vendor_code?: string | null;
  industry: string;
  gst_type: string;
  gstin: string;
  phone_number: string;
  company_email: string;
  contacts: {
    contact_id: string;
    name: string;
    designation: string;
    phone_number: string;
    email: string;
  }[];
}

interface ContactDetail {
  contact_id: string;
  name: string;
  designation: string;
  phone_number: string;
  email: string;
}

interface Agent {
  id: string;
  agent_name: string;
  agent_designation: string;
  agent_department: string;
}

interface AssignedMember {
  id: string;
  username: string;
}

interface EnquiryFormData {
  enquiry_date: string;
  source_of_enquiry: string;
  reference_number: string;
  initiated_by: string;
  company: Company | null;
  industry: string;
  contactPerson: ContactDetail[];
  agent: Agent | null;
  designation: string;
  department: string;
  members: AssignedMember[];
  terms_and_conditions: string;
  notes: string;
  description: string;
  item_details: any[];
  type: string;
  next_follow_up_date: string;
  remark: string;
  attachments: (File | string)[];
  tags?: string[];
  bill_from_address?: any;
}

const initialFormData: EnquiryFormData = {
  // enquiry_date: new Date().toISOString().split("T")[0],
  enquiry_date: getCurrentLocalDate(),
  initiated_by: "",
  source_of_enquiry: "",
  reference_number: "",
  company: null,
  industry: "",
  contactPerson: [],
  agent: null as Agent | null,
  designation: "",
  department: "",
  members: [],
  terms_and_conditions: "",
  notes: "",
  description: "",
  item_details: [],
  type: "",
  next_follow_up_date: "",
  remark: "",
  attachments: [],
  tags: [],
  bill_from_address: DEFAULT_ORGANISATION,
};

const validationSchema = Yup.object().shape({
  enquiry_date: Yup.string().required("Enquiry Date is required"),
  reference_number: Yup.string()
    .trim()
    .required("Reference Number is required"),
  initiated_by: Yup.string().required("Initiated by is required"),
  source_of_enquiry: Yup.string().required("Source of Enquiry is required"),
  company: Yup.object()
    .nullable()
    .test("company-required", "Please select a company", function (value) {
      if (!value) {
        return false;
      }
      const company = value as Company;
      if (!company.company_id || company.company_id === "") {
        return false;
      }
      return true;
    }),
  contactPerson: Yup.array().min(1, "At least one contact person is required"),
  industry: Yup.string().test(
    "industry-check",
    "Industry is required",
    function (value) {
      const { company } = this.parent;
      if (company && company.industry) {
        return true;
      }
      return !!value;
    },
  ),
  members: Yup.array().notRequired(),
  // item_details: Yup.array().min(1, "At least one product is required"),
  terms_and_conditions: Yup.string(),
  next_follow_up_date: Yup.string()
    .required("Next Follow Up Date is required")
    .test(
      "next-after-enquiry",
      "Next follow up date cannot be before enquiry date",
      function (value) {
        const { enquiry_date } = this.parent;

        if (!value || !enquiry_date) return true;

        // Compare only dates (no time issues)
        return new Date(value) >= new Date(enquiry_date);
      },
    ),
});

const ProductEnquiryFormV2: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);

  const [initialData, setInitialData] = useState<any>(initialEnquiryData);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [submitAction, setSubmitAction] = useState<"draft" | "print" | "save">(
    "save",
  );
  const [stage, setStage] = useState<any>("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<string[]>([]);
  const [isNotesOpen, setIsNotesOpen] = useState(true);
  const [existingDocuments, setExistingDocuments] = useState<any>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
    }
  };

  const handelstage = (stage: any) => {
    setStage(stage);
  };

  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Selectors
  const selectEnquiryData = createSelector(
    (state: any) => state.Enquiry,
    (enquiry) => ({
      categories: enquiry.categories,
      sub_categories: enquiry.subCategories,
      makes: enquiry.makes,
      models: enquiry.models,
      products: enquiry.products,
    }),
  );

  const { categories, sub_categories, makes, models, products } =
    useSelector(selectEnquiryData);

  const selectUsers = createSelector(
    (state: any) => state.users,
    (user) => ({
      usersFormDropDowns: user.userFormDropDowns || [],
    }),
  );

  const { usersFormDropDowns } = useSelector(selectUsers);

  const selectCompanyData = createSelector(
    (state: any) => state.Company,
    (company) => ({
      companiesData: company.companyList || [],
    }),
  );

  const { companiesData } = useSelector(selectCompanyData);

  const commonSources = [
    "Website",
    "Email",
    "Phone Call",
    "Visit",
    "Social Media",
    "Trade Show / Exhibition",
    "Partner / Vendor Referral",
    "Internal Reference",
    "Tender / RFP Portal",
    "Advertisement",
  ];

  const isEditMember = (m: any) => Boolean(m.user_id && !m.id);
  const normalizeMember = (m: any, users: any[]) => {
    // ✅ EDIT MODE → already normalized
    if (m.user_id && !m.id) {
      return {
        user_id: m.user_id,
        username: m.username,
        first_name: m.first_name || "",
        last_name: m.last_name || "",
        email: m.email || "",
        designation: m.designation || "",
        address: m.address || "",
        city: m.city || "",
        state: m.state || "",
        country: m.country || "",
        pin_code: m.pin_code || m.pincode || "",
        mobile_number: m.mobile_number || m.phone_number || "",
        user_type: m.user_type || "Staff",
      };
    }

    // ✅ CREATE MODE → lookup from dropdown
    const fullUser = users.find(
      (u: any) => u.user_id === m.user_id || u.id === m.id,
    );

    return {
      user_id: fullUser?.user_id || m.id,
      username: fullUser?.username || "",
      first_name: fullUser?.first_name || "",
      last_name: fullUser?.last_name || "",
      email: fullUser?.email || "",
      designation: fullUser?.designation || "",
      address: fullUser?.address || "",
      city: fullUser?.city || "",
      state: fullUser?.state || "",
      country: fullUser?.country || "",
      pin_code: fullUser?.pin_code || fullUser?.pincode || "",
      mobile_number: fullUser?.mobile_number || fullUser?.phone_number || "",
      user_type: fullUser?.user_type || "Staff",
    };
  };

  const formik = useFormik<EnquiryFormData>({
    initialValues: initialFormData,
    validationSchema: validationSchema,
    enableReinitialize: true,
    validate: (values) => {
      // const errors: any = {};
      // if (itemsRef.current.length === 0) {
      //   errors.item_details = "At least one product is required";
      // }
      // return errors;
    },
    onSubmit: async (values, { setSubmitting }) => {
      // if (itemsRef.current.length === 0) {
      //   formik.setFieldTouched("item_details", true);
      //   formik.setFieldError(
      //     "item_details",
      //     "At least one product is required",
      //   );
      //   setSubmitting(false);
      //   setLoading(false);
      //   return;
      // }

      // setLoading(true);
      // setSubmitting(true);

      try {
        const formData = new FormData();

        const payloadObject = {
          initiated_by: values.initiated_by || "",
          reference_number: values.reference_number || "",
          enquiry_stage: stage || "enquiry_log",
          source_of_enquiry: values.source_of_enquiry || "",
          description: values.description || "",
          bill_from: values.bill_from_address || null,
          enquiry_date: values.enquiry_date,
          enquiry_status:
            isEditMode && initialData?.status
              ? initialData.status
              : ENQ_STATUS.IN_PROGRESS,
          agent_name: values.agent?.agent_name || "",
          tags: values.tags && values.tags.length ? values.tags : [],
          notes: values.notes || "",
          action: submitAction,
          next_follow_up_date: values.next_follow_up_date || "",
          follow_up_remarks: values.remark || "",

          customer_details: {
            gstin: values.company?.gstin || "",
            gst_type: values.type || values.company?.gst_type || "",
            industry: values.industry || values.company?.industry || "",
            company_id: values.company?.company_id || "",
            company_name: values.company?.company_name || "",
            vendor_code: values.company?.vendor_code || "",
            branch_name: values.company?.branch_name || "",
            // sr_no: 1,
            phone_number: values.company?.phone_number || "",
            email: values.company?.company_email || "",
            address: (values.company as any)?.billing_address?.address || "",
            contact_details: values.contactPerson.map((cp: any) => ({
              contact_id: cp.contact_id,
              company_id: values.company?.company_id,
              name: cp.name,
              designation: cp.designation,
              email: cp.email,
              phone_number: cp.phone_number,
            })),
            billing_address: (values.company as any)?.billing_address || {},
            shipping_address: (values.company as any)?.shipping_addresses || [],
          },
          customer_name: values.company?.company_name || "",

          // assigned_members: values.members.map((m: any) => {
          //   const fullUser = usersFormDropDowns.find(
          //     (u: any) => u.user_id === m.user_id || u.id === m.id
          //   );
          //   return {
          //     user_id: fullUser?.user_id || m.id || m.user_id,
          //     username: fullUser?.username || m.username,
          //     first_name: fullUser?.first_name || "",
          //     last_name: fullUser?.last_name || "",
          //     email: fullUser?.email || "",
          //     designation: fullUser?.designation || "",
          //     address: (fullUser as any)?.address || "",
          //     city: (fullUser as any)?.city || "",
          //     state: (fullUser as any)?.state || "",
          //     country: (fullUser as any)?.country || "",
          //     pin_code:
          //       (fullUser as any)?.pin_code || (fullUser as any)?.pincode || "",
          //     mobile_number:
          //       (fullUser as any)?.mobile_number ||
          //       (fullUser as any)?.phone_number ||
          //       "",
          //     user_type: (fullUser as any)?.user_type || "Staff",
          //   };
          // }),
          assigned_members: values.members
            .map((m: any) => normalizeMember(m, usersFormDropDowns))
            .filter(
              (member, index, self) =>
                index === self.findIndex((x) => x.user_id === member.user_id),
            ),

          enquiry_product_items: items.map((item: any, index: number) => ({
            product_id: item.item || item.product_id,
            product_name: item.productName || item.item_name,
            category_name: item.category_name,
            sub_category_name: item.sub_category_name,
            make_name: item.make_name,
            model_name: item.model_name,
            unit_name: item.unit_name,
            unit_price: item.unit_price,
            hsn_code: item.hsn_code,
            gst_percentage: item.gst_percentage || item.gst_percent,
            remark: item.remark || "",
            product_sku: item.product_sku || item.sku,
            product_price: item.unit_price,
            product_quantity: item.quantity,
            product_total: item.total || item.total_price,
            created_by: item.created_by || "",
            updated_by: item.updated_by || "",
            product_type: item.product_type || [],
          })),

          existing_attachments: existingAttachments,
          // ...(isEditMode && id ? { id } : {}),
        };

        console.log("assigned_members payload", payloadObject.assigned_members);
        console.log("assigned_members in formik ", values.members);

        // Professionals recursive flattening of the payload object into formData
        appendToFormData(formData, payloadObject);
        // formData.append("data", JSON.stringify(payloadObject));

        // Files (appended separately using the specific field name expected by backend)
        // if (selectedFiles.length > 0) {
        //   selectedFiles.forEach((file) => {
        //     formData.append("attachments", file);
        //   });
        // }

        if (values.attachments?.length) {
          values.attachments.forEach((item: any) => {
            const file =
              item instanceof File ? item : item.file || item.originFileObj;

            if (file instanceof File) {
              formData.append("attachments", file); // ✅ NOT attachments[]
            }
          });
        }

        if (existingDocuments.length > 0) {
          formData.append(
            "existingDocuments",
            JSON.stringify(existingDocuments ?? []),
          );
        }

        await handleSaveProductEnquiry(formData, submitAction);
      } catch (err: any) {
        toast.error(err?.message || "Operation failed.");
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  useScrollToFormikError(formik.errors, formik.submitCount);

  useEffect(() => {
    formik.setFieldValue("item_details", items || []);
  }, [items]);

  // Initialize data
  // useEffect(() => {
  //   const init = async () => {
  //     await dispatch(fetchCompanyByDropDown());
  //     // dispatch(fetchCategories("products"));
  //     await dispatch(fetchAllUsersDropdown());

  //     try {
  //       if (id) {
  //         const enquiry = await dispatch(fetchProductEnquiryById(id)).unwrap();
  //         setInitialData(enquiry.data);
  //         mapDataToForm(enquiry.data, true);
  //       }
  //     } catch (err) {
  //       console.error("Failed to load initial product form data:", err);
  //       toast.error("Failed to load form data or dropdowns.");
  //     }
  //   };
  //   init();
  // }, [dispatch, id]);

  useEffect(() => {
    dispatch(fetchCompanyByDropDown());
    dispatch(fetchAllUsersDropdown());
  }, []);

  useEffect(() => {
    if (!id) return;
    if (!companiesData.length || !usersFormDropDowns.length) return;

    const loadProductEnquiry = async () => {
      try {
        const enquiry = await dispatch(fetchProductEnquiryById(id)).unwrap();

        setInitialData(enquiry.data);
        mapDataToForm(enquiry.data, true);
      } catch (err) {
        console.error("Failed to load initial product form data:", err);
        toast.error("Failed to load form data.");
      }
    };

    loadProductEnquiry();
  }, [id, usersFormDropDowns.length, companiesData.length]);

  const mapDataToForm = (data: any, isEditMode = false) => {
    const details = data;
    const customerDetails = details.customer_details || {};

    const companyIdFromApi = customerDetails.company_id || details.company;
    const companyObj =
      companiesData.find((c: any) => c.company_id === companyIdFromApi) || null;

    // Map contact persons from the API response
    const apiContacts = customerDetails.contact_details || [];
    const contactPersonList = apiContacts.map((c: any) => ({
      contact_id: c.contact_id,
      name: c.name,
      designation: c.designation,
      phone_number: c.phone_number,
      email: c.email,
    }));

    const agentData =
      details.agent_name || details.agent
        ? {
            agent_name:
              details.agent_name || details.agent?.agent_name || "N/A",
            agent_designation: details.agent?.agent_designation || "",
            agent_department: details.agent?.agent_department || "",
          }
        : null;

    const assignedMembersFromApi =
      details.assigned_members || details.assigned_member_details || [];

    const uniqueMembersMap = new Map<string, any>();

    assignedMembersFromApi.forEach((member: any) => {
      const resolvedMember =
        usersFormDropDowns.find(
          (m: any) =>
            m.user_id === member.user_id ||
            m.user_id === member.id ||
            m.user_id === member.user,
        ) || member;

      const key = resolvedMember.user_id || resolvedMember.id;
      if (key && !uniqueMembersMap.has(key)) {
        uniqueMembersMap.set(key, resolvedMember);
      }
    });

    const mappedMembers = Array.from(uniqueMembersMap.values());

    const apiProducts =
      details.enquiry_product_items ||
      details.items_detail ||
      details.item_details ||
      [];
    const mappedItems = apiProducts.map((item: any) => ({
      ...item,
      id: item.enquiry_product_item_id || item.id || Date.now() + Math.random(),
      item: item.product_id || item.item,
      productName: item.product_name || item.item_name,
      item_name: item.product_name || item.item_name,
      quantity: item.product_quantity || item.quantity,
      unit_price: item.unit_price || item.product_price,
      total: item.product_total || item.total || item.total_price,
      gst_percentage: item.gst_percentage || item.gst_percent,
    }));

    const tags = details.tags || [];

    formik.setValues({
      enquiry_date: details?.enquiry_date
        ? new Date(details.enquiry_date).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      source_of_enquiry: details?.source_of_enquiry || "",
      reference_number: details?.reference_number || "",
      initiated_by: details?.initiated_by || "",
      company: companyObj,
      industry:
        customerDetails.industry ||
        details.industry ||
        companyObj?.industry ||
        "",
      contactPerson: contactPersonList,
      agent: agentData ? ({ ...agentData, id: "" } as Agent) : null,
      designation: agentData?.agent_designation || "",
      department: agentData?.agent_department || "",
      members: mappedMembers,
      item_details: [],
      terms_and_conditions: details?.terms_and_conditions || "",
      notes: details?.notes || "",
      description: details?.description || "",
      type:
        customerDetails.gst_type || details.type || companyObj?.gst_type || "",
      next_follow_up_date:
        details?.enquiry_follow_ups?.[0]?.next_follow_up_date.split("T")[0] ||
        "",
      remark: details?.enquiry_follow_ups?.[0]?.follow_up_remarks || "",
      attachments: [],
      tags: tags,
      bill_from_address: details?.bill_from || DEFAULT_ORGANISATION,
    });

    setItems(mappedItems);
    setExistingDocuments(details?.attachments || []);
    // if (details.attachments) {
    //   setExistingAttachments(details.attachments);
    // }
  };

  const handleProductAdd = (item: any) => {
    setItems((prevItems) => {
      const newItems = [...prevItems, item];
      if (newItems.length > 0 && formik.errors.item_details) {
        formik.setFieldError("item_details", undefined);
      }
      return newItems;
    });
  };

  useEffect(() => {
    if (items.length > 0 && formik.errors.item_details) {
      formik.setFieldError("item_details", undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  // Auto-open notes section if notes have content
  useEffect(() => {
    if (formik.values.notes && formik.values.notes.trim() !== "") {
      setIsNotesOpen(true);
    }
  }, [formik.values.notes]);

  const handleSaveProductEnquiry = async (formData: FormData, action: any) => {
    try {
      if (isEditMode) {
        setIsSaving(true);
        await dispatch(updateProductEnquiry({ id: id!, formData })).unwrap();
        toast.success("Product Enquiry updated successfully");
        navigate("/enquiry/product/enquiry_log");
      } else {
        if (action.toLowerCase() === "draft") {
          setIsSavingDraft(true);
          await dispatch(createProductEnquiry(formData)).unwrap();
          toast.success("Product Enquiry has been saved as a draft");
          navigate("/enquiry/product/draft");
          clearTableState(
            TableKeys.ENQUIRY.PRODUCT,
            undefined,
            "draft", // "enquiry_log" | "draft"
          );
        } else {
          setIsSaving(true);
          await dispatch(createProductEnquiry(formData)).unwrap();
          toast.success("Product Enquiry created successfully");
          clearTableState(
            TableKeys.ENQUIRY.PRODUCT,
            undefined,
            "enquiry_log", // "enquiry_log" | "draft"
          );
          navigate("/enquiry/product/enquiry_log");
        }
      }
    } catch (err: any) {
      console.log("Error from enquiry", err);
      toast.error("Failed to Create Enquiry Service ");
      // throw new Error(err?.message || "Product Enquiry operation failed");
    } finally {
      setIsSaving(false);
      setIsSavingDraft(false);
    }
  };

  const handleAssignedMembersChange = (newMembers: any[]) => {
    formik.setFieldValue("members", newMembers);
  };

  // Tags are managed via reusable AddTags component and stored in formik.values.tags

  return (
    <>
      <style>
        {`
                              .accordion-terms-conditions .accordion-button,
                              .accordion-notes .accordion-button,
                              .accordion-attach-files .accordion-button {
                                background-color: ${MecordColors?.LightGrayColor} !important;
                               color: ${MecordColors?.NormalText} !important;
                              }
                              .accordion-terms-conditions .accordion-button:not(.collapsed),
                              .accordion-notes .accordion-button:not(.collapsed),
                              .accordion-attach-files .accordion-button:not(.collapsed) {
                                background-color: ${MecordColors?.LightGrayColor} !important;
                                color: ${MecordColors?.NormalText} !important;
                              }


                .tag-add-btn {
                position: absolute;
                right: 6px;
                top: 50%;
                transform: translateY(-50%);
                
                width: 22px;
                height: 22px;
                border-radius: 50%;
                border: none;
                
                background: #dc3545;
                color: #fff;

                display: flex;
                align-items: center;
                justify-content: center;

                font-size: 14px;
                line-height: 1;
                padding: 0;
                cursor: pointer;
              }

              .tag-add-btn:hover {
                background: #bb2d3b;
              }
                            `}
      </style>

      <ENQPageContainer>
        <PageBreadcrumb
          title={isEditMode ? "Edit Sales Enquiry" : "Add Sales Enquiry"}
          titleLink="/enquiry/product/enquiry_log"
          breadcrumbs={[
            {
              label: "Sales Enquiry",
              to: "/enquiry/product/enquiry_log",
            },
            {
              label: isEditMode ? "Edit Sales Enquiry" : "Add Sales Enquiry",
              to: "",
            },
          ]}
          backNavigationPath={-1}
        />
        <hr />
        {isEditMode && initialData === initialEnquiryData ? (
          <LoadingSpinner />
        ) : loading ? (
          <LoadingSpinner />
        ) : (
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              setSubmitAction("save");
              formik.setFieldTouched("company", true);
              formik.setFieldTouched("contactPerson", true);
              formik.setFieldTouched("industry", true);
              formik.setFieldTouched("item_details", true);
              formik.handleSubmit(e);
            }}
          >
            {/* Enquiry & Customer Details */}
            <Row className="mb-3">
              <Col lg={6} md={12}>
                <h5 className="fw-semibold mb-3">Enquiry Details</h5>
                <EnquiryDetailsSection
                  formik={formik}
                  isEdit={isEditMode}
                  sources={commonSources}
                />
              </Col>

              <Col lg={6} md={12}>
                <div className="d-flex gap-2 mb-2">
                  <h5 className="fw-semibold ">Customer Details</h5>
                  <Button
                    size="sm"
                    variant="light"
                    className="fw-semibold"
                    onClick={() => setShowCompanyModal(true)}
                    style={{
                      backgroundColor: MecordColors?.LightGrayColor,
                    }}
                  >
                    + Add New Customer
                  </Button>
                </div>
                <CustomerDetailsSection formik={formik} />
              </Col>
            </Row>

            <hr />

            {/* Billing Details section */}
            <Row className="mb-3">
              <Col lg={6} md={12}>
                <h5 className="fw-semibold mb-3">Billing Details</h5>
                <div
                  className="p-3 rounded mb-3"
                  style={{ backgroundColor: MecordColors?.LightGrayColor }}
                >
                  <Row>
                    <Col lg={12} md={12}>
                      <BillingFromAddress
                        value={formik.values.bill_from_address}
                        onChange={async (newValue) => {
                          await formik.setFieldValue("bill_from_address", newValue);
                          formik.setFieldTouched("bill_from_address", true);
                        }}
                        onBlur={async () => {
                          formik.setFieldTouched("bill_from_address", true);
                        }}
                        error={formik.touched.bill_from_address ? (formik.errors.bill_from_address as string) : undefined}
                      />
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>

            <hr />

            {/* Agent & Members Details */}
            <Row className="mb-3">
              <Col lg={6} md={12}>
                <h5 className="fw-semibold mb-3">Agent Details</h5>
                <Col className="rounded mb-3" md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Agent Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="agent"
                      placeholder="Enter Agent Name"
                      value={formik.values.agent?.agent_name || ""}
                      onChange={(e) => {
                        formik.setFieldValue("agent", {
                          ...formik.values.agent,
                          agent_name: e.target.value,
                        });
                      }}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        formik.setFieldTouched("agent", true);
                      }}
                      isInvalid={!!formik.errors.agent && formik.touched.agent}
                    />
                    {formik.errors.agent && formik.touched.agent && (
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.agent as string}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>

                  <AddTags
                    label="Add Tags"
                    placeholder="Enter Tags"
                    value={formik.values.tags || []}
                    onChange={(tags) => formik.setFieldValue("tags", tags)}
                  />
                </Col>
              </Col>

              <Col lg={6} md={12}>
                <h5 className="fw-semibold mb-3">Add Members</h5>
                <Form.Label>
                  Assign Members
                  {/* <span className="text-danger">*</span> */}
                </Form.Label>

                {/* Autocomplete for adding members */}
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={usersFormDropDowns || []}
                  getOptionLabel={(option: any) => {
                    // Handle new structure: first_name + last_name
                    if (option.first_name || option.last_name) {
                      return (
                        `${option.first_name || ""} ${
                          option.last_name || ""
                        }`.trim() ||
                        option.username ||
                        option.email ||
                        ""
                      );
                    }
                    // Handle old structure: fullname
                    return (
                      option.fullname ||
                      option.username ||
                      option.user_name ||
                      ""
                    );
                  }}
                  isOptionEqualToValue={(option: any, value: any) => {
                    // Handle new structure: user_id
                    if (option.user_id && value.user_id) {
                      return option.user_id === value.user_id;
                    }
                    // Handle old structure: id
                    return (
                      (option.id || option.user_id) ===
                      (value.id || value.user_id)
                    );
                  }}
                  value={(formik.values.members || []).map((member: any) => {
                    // Ensure each member has the correct structure
                    if (member.user_id && !member.id) {
                      return { ...member, id: member.user_id };
                    }
                    return member;
                  })}
                  onChange={(_, newValue) => {
                    // Map newValue to ensure consistent structure
                    const mappedValue = newValue.map((member: any) => ({
                      ...member,
                      id: member.user_id || member.id,
                      fullname:
                        member.first_name && member.last_name
                          ? `${member.first_name} ${member.last_name}`.trim()
                          : member.fullname || member.username || "",
                    }));
                    formik.setFieldValue("members", mappedValue);
                  }}
                  onBlur={() => {
                    formik.setFieldTouched("members", true);
                    formik.validateField("members");
                  }}
                  renderOption={(props, option: any, { selected }) => {
                    const displayName =
                      option.first_name || option.last_name
                        ? `${option.first_name || ""} ${
                            option.last_name || ""
                          }`.trim()
                        : option.fullname ||
                          option.username ||
                          option.user_name ||
                          option.email ||
                          "";

                    return (
                      <li {...props} style={{ padding: 0 }}>
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.8,
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            cursor: "pointer",

                            // ✅ Hover state
                            "&:hover": {
                              backgroundColor: MecordColors.Hover,
                              color: MecordColors.ActiveText,
                            },

                            // ✅ Selected state
                            ...(selected && {
                              backgroundColor: MecordColors.ActiveBackground,
                              color: MecordColors.ActiveText,
                            }),
                          }}
                        >
                          {/* Member Name */}
                          <span>{displayName}</span>

                          {/* ❌ Remove from selection */}
                          {selected && (
                            <span
                              style={{
                                cursor: "pointer",
                                marginLeft: "10px",
                                color: "#d32f2f",
                                fontWeight: "bold",
                              }}
                              onClick={(e) => {
                                e.stopPropagation(); // prevent option toggle

                                const updatedMembers = (
                                  formik.values.members || []
                                ).filter(
                                  (m: any) =>
                                    (m.user_id || m.id) !==
                                    (option.user_id || option.id),
                                );

                                formik.setFieldValue("members", updatedMembers);
                              }}
                            >
                              ✕
                            </span>
                          )}
                        </Box>
                      </li>
                    );
                  }}
                  renderTags={() => null} // hide default chips
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Members"
                      size="small"
                      sx={autocompleteDropdownStyles.inputSx}
                      error={formik.touched.members && !!formik.errors.members}
                      helperText={
                        formik.touched.members
                          ? Array.isArray(formik.errors.members)
                            ? "Invalid member selection"
                            : (formik.errors.members as string)
                          : ""
                      }
                    />
                  )}
                />

                {/* Display selected members as chips with remove button */}
                <Row>
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {(formik.values.members || []).map((member: any, index) => (
                      <div
                        key={member.id || member.user_id || index}
                        className="d-flex align-items-center px-2 py-1 rounded"
                        style={{
                          background: MecordColors?.LightThemeBGColor,
                          border: `1px solid ${MecordColors?.ThemeBGColor}`,
                          fontSize: "14px",
                        }}
                      >
                        {member.first_name && member.last_name
                          ? `${member.first_name} ${member.last_name}`.trim()
                          : member.fullname ||
                            member.username ||
                            member.email ||
                            ""}
                        <span
                          style={{
                            marginLeft: "8px",
                            cursor: "pointer",
                            color: "red",
                          }}
                          onClick={() => {
                            const updated = (
                              formik.values.members || []
                            ).filter((_, i) => i !== index);
                            formik.setFieldValue("members", updated);
                          }}
                        >
                          ✕
                        </span>
                      </div>
                    ))}
                  </div>
                </Row>
              </Col>
            </Row>

            <hr />

            {/* Product Section */}
            <Row className="p-2" id="item_details">
              <div className="d-flex gap-4 mb-2">
                <h5 className="fw-semibold mb-2 ">Product</h5>
                <Button
                  size="sm"
                  variant="light"
                  className="fw-semibold"
                  onClick={() => setShowProductModal(true)}
                  style={{
                    backgroundColor: MecordColors?.LightGrayColor,
                    borderRadius: "5px",
                  }}
                >
                  + Add New Product
                </Button>
              </div>
              <ProductFormTable
                items={items}
                setItems={setItems}
                onAdd={handleProductAdd}
                onAddNewProduct={() => setShowProductModal(true)}
              />
              {/* {formik.errors.item_details && formik.touched.item_details && (
                <div
                  className="text-danger"
                  style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}
                >
                  {typeof formik.errors.item_details === "string"
                    ? formik.errors.item_details
                    : "At least one product is required"}
                </div>
              )} */}
            </Row>

            <hr />

            {/* Notes Section */}
            <Row className="mb-3">
              <Col lg={6} md={12}>
                <div className="d-flex justify-content-between align-items-center mb-1 gap-2">
                  <h5 className="fw-semibold">Notes & Attachments</h5>
                </div>

                <div
                  className="rounded mb-3"
                  style={{ backgroundColor: MecordColors?.LightGrayColor }}
                >
                  {/* Notes Header - Collapsible */}
                  <div
                    className="d-flex justify-content-start align-items-center p-2 rounded"
                    style={{
                      cursor:
                        !formik.values.notes ||
                        formik.values.notes.trim() === ""
                          ? "pointer"
                          : "default",
                      backgroundColor: MecordColors?.LightGrayColor,
                    }}
                    onClick={() => {
                      // Only allow closing if notes are empty
                      if (
                        !formik.values.notes ||
                        formik.values.notes.trim() === ""
                      ) {
                        setIsNotesOpen(!isNotesOpen);
                      }
                    }}
                  >
                    {(!formik.values.notes ||
                      formik.values.notes.trim() === "") && (
                      <div className="me-1">
                        {isNotesOpen ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </div>
                    )}

                    <h6
                      className="fw-semibold mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      Notes
                    </h6>
                  </div>

                  {/* Notes Content - Collapsible */}
                  {isNotesOpen && (
                    <div className="p-0">
                      <Form.Group>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="notes"
                          placeholder="Enter your notes"
                          value={formik.values.notes || ""}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          style={{ maxHeight: "400px", resize: "vertical" }}
                          isInvalid={
                            formik.touched.notes && !!formik.errors.notes
                          }
                        />

                        <Form.Control.Feedback type="invalid">
                          {formik.touched.notes && formik.errors.notes}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>
                  )}
                </div>

                <div>
                  <h6 className="fw-semibold mb-2">Attach Files</h6>

                  <Form.Group>
                    {/* <Form.Label>Attach Files</Form.Label> */}
                    {/* <Form.Control
                      type="file"
                      name="files"
                      multiple
                      onChange={handleFileChange}
                    />
                     */}

                    <FileUpload
                      label=""
                      multiple={true}
                      value={formik.values.attachments} // 👈 NEW FILES ONLY
                      existingFiles={existingDocuments} // 👈 EXISTING DOCS
                      onChange={(files) =>
                        formik.setFieldValue("attachments", files)
                      }
                      onRemoveExisting={(index) => {
                        const updatedDocs = [...existingDocuments];
                        updatedDocs.splice(index, 1);
                        setExistingDocuments(updatedDocs);

                        // Optional: track deleted docs for backend
                        // setDeletedDocuments([...deletedDocuments, existingDocuments[index]]);
                      }}
                    />
                  </Form.Group>

                  {isEditMode && existingAttachments.length > 0 && (
                    <div className="mt-3">
                      <h5
                        className="fw-semibold mb-2"
                        style={{ fontSize: "0.85rem" }}
                      >
                        Existing Attachments:
                      </h5>
                      <div className="d-flex flex-wrap gap-2">
                        {existingAttachments.map((url, index) => {
                          const fileName = url.split("/").pop();
                          return (
                            <a
                              key={index}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="d-flex align-items-center p-2 rounded border bg-white text-decoration-none"
                              style={{
                                fontSize: "0.8rem",
                                color: MecordColors.ThemeBGColor,
                              }}
                            >
                              <i className="ri-file-line me-2"></i>
                              <span
                                className="text-truncate"
                                style={{ maxWidth: "150px" }}
                              >
                                {fileName}
                              </span>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </Col>

              {!isEditMode && (
                <Col md={6}>
                  <h5 className="fw-semibold mb-2">Schedule Follow-Up</h5>
                  <div
                    className="p-2 rounded  mb-3"
                    style={{ backgroundColor: MecordColors?.LightGrayColor }}
                  >
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Next Follow Up Date{" "}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="next_follow_up_date"
                        value={formik.values.next_follow_up_date}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        //   readOnly={isEdit}
                        isInvalid={
                          !!(
                            formik.errors.next_follow_up_date &&
                            formik.touched.next_follow_up_date
                          )
                        }
                      />
                      {formik.errors.next_follow_up_date &&
                        formik.touched.next_follow_up_date && (
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.next_follow_up_date as string}
                          </Form.Control.Feedback>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Remark</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="remark"
                        placeholder="Enter remark"
                        value={formik.values.remark}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </Form.Group>
                  </div>
                </Col>
              )}
            </Row>

            <hr />

            {/* Action Buttons */}
            <Row className="mt-3">
              <Col className="d-flex justify-content-center gap-3">
                <FormActionButtons
                  onCancel={() => navigate(-1)}
                  showSaveDraft={!isEditMode}
                  onSaveDraft={async () => {
                    // Mark all fields as touched to show validation errors
                    formik.setFieldTouched("company", true);
                    formik.setFieldTouched("contactPerson", true);
                    formik.setFieldTouched("industry", true);
                    formik.setFieldTouched("reference_number", true);
                    // formik.setFieldTouched("item_details", true);

                    // Validate form
                    const errors = await formik.validateForm();

                    // If there are validation errors, don't save
                    // if (Object.keys(errors).length > 0) {
                    //   toast.error(
                    //     "Please fix validation errors before saving as draft",
                    //   );
                    //   return;
                    // }

                    // If validation passes, save as draft
                    setSubmitAction("draft");
                    setStage("draft");
                    formik.handleSubmit();
                  }}
                  onSave={async () => {
                    // Mark all fields as touched to show validation errors
                    formik.setFieldTouched("company", true);
                    formik.setFieldTouched("contactPerson", true);
                    formik.setFieldTouched("industry", true);
                    formik.setFieldTouched("reference_number", true);
                    // formik.setFieldTouched("item_details", true);

                    // Validate form
                    const errors = await formik.validateForm();

                    // If there are validation errors, don't save
                    // if (Object.keys(errors).length > 0) {
                    //   toast.error(
                    //     "Please fix validation errors before saving the enquiry",
                    //   );
                    //   return;
                    // }

                    // If validation passes, submit the form
                    setSubmitAction("save");
                    formik.handleSubmit();
                  }}
                  isSaving={isSaving}
                  isSavingDraft={isSavingDraft}
                />
              </Col>
            </Row>
          </Form>
        )}

        {/* Modal Section: Product & Customer */}

        <>
          <style>
            {`
                /* Make React-Bootstrap white close button fully bright */
                .bright-white-close .btn-close {
                  filter: invert(1) brightness(2); /* turns faded white to bright white */
                  opacity: 1;                       /* ensure fully visible */
                }
                .bright-white-close .btn-close:hover {
                  filter: invert(1) brightness(2.5); /* slightly brighter on hover */
                }
            `}
          </style>
          {/* OLD Customer pop up  */}
          {/* <Modal
            show={showCompanyModal}
            onHide={() => setShowCompanyModal(false)}
            backdrop="static"
            keyboard={false}
            size="xl"
            centered
          >
            <Modal.Header
              closeButton
              closeVariant="white"
              style={{
                backgroundColor: MecordColors?.ThemeBGColor,
                paddingBottom: "20px",
              }}
              className="bright-white-close"
            >
              <Modal.Title style={{ color: "#fff" }}>
                Add New Customer
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
              <CompanyFormModal
                onSuccess={async () => {
                  await dispatch(fetchCompanyByDropDown());
                  setShowCompanyModal(false);
                }}
                onCancel={() => setShowCompanyModal(false)}
              />
            </Modal.Body>
          </Modal> */}

          {/* Add Customer Modal */}
          <AddCustomerModal
            show={showCompanyModal}
            onHide={() => setShowCompanyModal(false)}
            onSave={(companyData: any) => {
              // TODO: Implement save logic
              // In a real app, this would make an API call to save the company
              // and then refresh the company options list
              console.log("Company data to save:", companyData);
            }}
          />

          <AddProductModal
            show={showProductModal}
            onHide={() => setShowProductModal(false)}
            onSave={async () => {
              await dispatch(fetchCategories("products"));
            }}
          />
        </>
      </ENQPageContainer>
    </>
  );
};

export default ProductEnquiryFormV2;
